import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, Stack,
  CircularProgress, Alert, Snackbar, IconButton, Tooltip, Menu,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TuneIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/adminApi';

const STATUS_COLOR = { ACTIVE: 'success', SUSPENDED: 'warning', PENDING: 'default', DEACTIVATED: 'error' };
const PLAN_COLOR = { trial: 'default', paid: 'primary' };
const fmtUsd = (n) => (n === null || n === undefined ? '—' : `$${Number(n).toFixed(2)}`);

const EMPTY_FORM = { name: '', adminEmail: '', plan: 'trial' };

export default function TenantsListPage() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Action menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTenant, setMenuTenant] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 100 };
      if (statusFilter) { params.status = statusFilter; }
      const data = await adminApi.listTenants(params);
      setTenants(data.items || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = tenants.filter((t) =>
    !search || t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.adminEmail?.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async () => {
    setSaving(true);
    try {
      await adminApi.createTenant(form);
      setToast({ open: true, message: `Tenant "${form.name}" created.`, severity: 'success' });
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { setToast({ open: true, message: e.message, severity: 'error' }); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (tenantId, status) => {
    try {
      await adminApi.updateTenantStatus(tenantId, status);
      setToast({ open: true, message: `Status updated to ${status}.`, severity: 'success' });
      load();
    } catch (e) { setToast({ open: true, message: e.message, severity: 'error' }); }
    setMenuAnchor(null);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>Tenants</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New Tenant
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
        <TextField
          size="small" placeholder="Search by name or email…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="SUSPENDED">Suspended</MenuItem>
            <MenuItem value="DEACTIVATED">Deactivated</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Revenue/mo</TableCell>
              <TableCell align="right">AWS Cost (30d)</TableCell>
              <TableCell>Admin Email</TableCell>
              <TableCell>Users</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No tenants found.</TableCell></TableRow>
            ) : filtered.map((t) => (
              <TableRow key={t.tenantId} hover>
                <TableCell>
                  <Typography
                    variant="body2" fontWeight={600} color="primary"
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => navigate(`/tenants/${t.tenantId}`)}
                  >
                    {t.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={t.plan?.toUpperCase()} size="small" color={PLAN_COLOR[t.plan] || 'default'} />
                </TableCell>
                <TableCell>
                  <Chip label={t.status} size="small" color={STATUS_COLOR[t.status] || 'default'} variant="outlined" />
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>{fmtUsd(t.mrr)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13, color: 'error.main' }}>{fmtUsd(t.awsCost)}</TableCell>
                <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{t.adminEmail}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{t.settings?.maxUsers ?? '—'} max</TableCell>
                <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>
                  {new Date(t.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title="Manage Features">
                      <IconButton size="small" onClick={() => navigate(`/tenants/${t.tenantId}/features`)}>
                        <TuneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Manage Users">
                      <IconButton size="small" onClick={() => navigate(`/tenants/${t.tenantId}/users`)}>
                        <PeopleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuTenant(t); }}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Action menu */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {menuTenant?.status !== 'ACTIVE' && (
          <MenuItem onClick={() => handleStatusChange(menuTenant.tenantId, 'ACTIVE')}>Activate</MenuItem>
        )}
        {menuTenant?.status === 'ACTIVE' && (
          <MenuItem onClick={() => handleStatusChange(menuTenant.tenantId, 'SUSPENDED')}>Suspend</MenuItem>
        )}
        <MenuItem
          onClick={() => handleStatusChange(menuTenant?.tenantId, 'DEACTIVATED')}
          sx={{ color: 'error.main' }}
        >
          Deactivate
        </MenuItem>
      </Menu>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Tenant</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Company Name *" size="small" fullWidth value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            <TextField label="Admin Email *" size="small" fullWidth type="email" value={form.adminEmail}
              onChange={(e) => setForm((f) => ({ ...f, adminEmail: e.target.value }))} />
            <FormControl size="small" fullWidth>
              <InputLabel>Plan</InputLabel>
              <Select value={form.plan} label="Plan" onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value }))}>
                <MenuItem value="trial">Trial</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving || !form.name || !form.adminEmail}>
            {saving ? 'Creating…' : 'Create Tenant'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
